import type { ArtefactSummary, TopicSummary } from './api.ts'

/** One document-type slice of a topic: `{ type: 'form', label: 'forms', count: 8 }`. */
export type Composition = { type: string; label: string; count: number }

export type CatalogEntry = {
  kind: 'topic' | 'artefact'
  id: string
  title: string
  department: string
  /** Quality area(s) — a topic has exactly one, an artefact may carry several. */
  qualityArea: string
  to: string
  isDraft: boolean
  meta: string
  /** Topic entries only: the mix of documents inside the bundle. */
  composition?: Composition[]
}

/** The NQF's seven Quality Areas, in the order an assessor works through them. */
export const QUALITY_AREAS = ['QA1', 'QA2', 'QA3', 'QA4', 'QA5', 'QA6', 'QA7']

// Astute's "tools" are printable forms; "Tool" is reserved for the AI-assisted ones.
export const CONTENT_TYPES = [
  { value: 'Tool', label: 'Tools' },
  { value: 'Policy', label: 'Policies' },
  { value: 'Procedure', label: 'Procedures' },
  { value: 'Form', label: 'Forms & Checklists' },
]

/** Document types in the order they sit behind a tab in the physical manual. */
const TYPE_ORDER = [
  { type: 'policy', one: 'policy', many: 'policies' },
  { type: 'procedure', one: 'procedure', many: 'procedures' },
  { type: 'form', one: 'form', many: 'forms' },
]

type Filters = { qa?: string | null; type?: string | null }

const hasTag = (a: ArtefactSummary, dimension: string, value: string) =>
  a.tags.some((t) => t.dimension === dimension && t.value === value)

const matchesFilters = (a: ArtefactSummary, filters?: Filters) =>
  (!filters?.qa || hasTag(a, 'quality_area', filters.qa)) &&
  (!filters?.type || hasTag(a, 'content_type', filters.type))

const contentType = (a: ArtefactSummary) =>
  a.tags.find((t) => t.dimension === 'content_type')?.value ?? ''

const qualityAreas = (a: ArtefactSummary) =>
  a.tags.filter((t) => t.dimension === 'quality_area').map((t) => t.value).join(', ')

/**
 * The document mix inside a topic, policy-first. Zero counts are dropped, so a
 * bundle filtered down to one type describes only that type.
 */
const composition = (members: ArtefactSummary[]): Composition[] =>
  TYPE_ORDER.flatMap(({ type, one, many }) => {
    const count = members.filter((a) => a.type === type).length
    return count === 0 ? [] : [{ type, label: count === 1 ? one : many, count }]
  })

/**
 * Groups the flat artefact catalog into one card per topic — a policy plus its
 * procedures and forms, read as a single bundle — and one card per bespoke
 * tool. Pure over its inputs so Dashboard and DepartmentPage render the exact
 * same shape from the exact same rule; HubLanding's per-department counts use
 * it too, for the same reason belongsToDepartment (departments.ts) is shared
 * rather than reimplemented per view — Task 11 shipped a bug from exactly that
 * kind of duplication.
 */
export function buildEntries(
  artefacts: ArtefactSummary[],
  topics: TopicSummary[],
  filters?: Filters,
): CatalogEntry[] {
  const entries: CatalogEntry[] = []

  for (const topic of topics) {
    const members = artefacts.filter((a) => a.topic_id === topic.id)
    const matching = members.filter((a) => matchesFilters(a, filters))
    // No member survives the active filters (or the topic has none at all) —
    // nothing to show, so the card itself disappears rather than reading "0 documents".
    if (matching.length === 0) continue

    entries.push({
      kind: 'topic',
      id: topic.id,
      title: topic.title,
      department: topic.department,
      qualityArea: topic.quality_area,
      to: `/topics/${topic.id}`,
      composition: composition(matching),
      // Draft if the topic itself is unapproved, or any member is — regardless
      // of the current filter, since the safety property is about the bundle
      // as a whole, not just what's currently visible.
      isDraft: topic.review_status !== 'approved' || members.some((a) => a.review_status !== 'approved'),
      meta: `${topic.quality_area} · ${matching.length} document${matching.length === 1 ? '' : 's'}`,
    })
  }

  for (const a of artefacts) {
    if (a.topic_id) continue // belongs to a topic card above, not its own
    if (!matchesFilters(a, filters)) continue
    entries.push({
      kind: 'artefact',
      id: a.id,
      title: a.title,
      department: a.department,
      qualityArea: qualityAreas(a),
      to: `/tools/${a.id}`,
      isDraft: a.review_status !== 'approved',
      meta: contentType(a),
    })
  }

  entries.sort((x, y) => {
    if (x.kind !== y.kind) return x.kind === 'topic' ? -1 : 1
    return x.title.localeCompare(y.title)
  })

  return entries
}

export type Facet = { value: string; label: string; count: number }

/**
 * The filters worth offering here, each carrying how many cards it would leave
 * on screen. Counts come from buildEntries itself, so a chip can never promise
 * a result the grid won't show.
 *
 * A facet is dropped when it would strand the user on an empty page — offering
 * QA6 in a department that holds nothing under QA6 is a control whose only
 * outcome is "nothing matches". The currently-selected facet always survives,
 * or clearing it would mean clicking a chip that is no longer there.
 *
 * Each dimension is counted with the *other* dimension's filter applied but not
 * its own — standard faceted-search behaviour, and the reason picking QA2 never
 * makes the QA4 chip vanish underneath the pointer.
 */
export function buildFacets(
  artefacts: ArtefactSummary[],
  topics: TopicSummary[],
  filters: Filters,
): { qa: Facet[]; type: Facet[] } {
  const count = (f: Filters) => buildEntries(artefacts, topics, f).length

  return {
    qa: QUALITY_AREAS
      .map((value) => ({ value, label: value, count: count({ qa: value, type: filters.type }) }))
      .filter((f) => f.count > 0 || f.value === filters.qa),
    type: CONTENT_TYPES
      .map(({ value, label }) => ({ value, label, count: count({ qa: filters.qa, type: value }) }))
      .filter((f) => f.count > 0 || f.value === filters.type),
  }
}
